import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Table, Tabs, Tag, Input, Select, Button, Spin, Tooltip, Modal, Switch } from 'antd';
import Layout from '../../components/Layout';
import {
  FireOutlined, AlertOutlined, WarningOutlined, CheckCircleOutlined,
  SendOutlined, ReloadOutlined, SearchOutlined,
  ClockCircleOutlined, BellFilled,
} from '@ant-design/icons';
import { toast } from 'react-toastify';
import axiosInstance from '../../variables/api/axiosInstance';
import { formatDate, formatMontant } from '../../variables/constants';
import RelancePanel from './RelancePanel';
import RelancesBilateralesTab from './RelancesBilateralesTab';
import HistoriqueRelancesTab from './HistoriqueRelancesTab';
import RelancesParOrganismeTab from './RelancesParOrganismeTab';
import TableExportButton from '../../components/TableExportButton';
import KpiCards from '../../components/KpiCards';
import PageHeader from '../../components/PageHeader';
import { filtreSansAccents } from '../../utils/rechercheTexte';
import i18n from '../../i18n';
import { avecLibelleTraduit, libellesTraduits } from '../../i18n/libelles';
import { libelleDelai } from '../../i18n/donnees';

const GREEN = '#1a6b3c';

/* ── Niveaux ─────────────────────────────────────────────────────── */
const NIVEAUX = avecLibelleTraduit({
  CRITIQUE:  { color: '#dc2626', bg: '#fee2e2', border: '#fecaca', icon: <FireOutlined />        },
  URGENT:    { color: '#ea580c', bg: '#fff7ed', border: '#fed7aa', icon: <AlertOutlined />       },
  ATTENTION: { color: '#ca8a04', bg: '#fefce8', border: '#fde68a', icon: <WarningOutlined />     },
  NORMAL:    { color: '#15803d', bg: '#f0fdf4', border: '#bbf7d0', icon: <CheckCircleOutlined /> },
}, 'relances:niveaux');

const CATEGORIES = libellesTraduits('relances:categories');

/* ── CSS global (injecté une fois) ───────────────────────────────── */
let _injected = false;
function injectStyles() {
  if (_injected || typeof document === 'undefined') return;
  _injected = true;
  const s = document.createElement('style');
  s.textContent = `
    @keyframes rl-panelIn { from { transform:translateX(36px); opacity:0; } to { transform:translateX(0); opacity:1; } }
    .rl-row:hover td { background: #f0fdf4 !important; cursor:pointer; }
    .rl-row-selected td { background: #dcfce7 !important; }
    .rl-thin-sb::-webkit-scrollbar { width: 4px; }
    .rl-thin-sb::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 4px; }
    .rl-thin-sb::-webkit-scrollbar-track { background: transparent; }
  `;
  document.head.appendChild(s);
}

/* ═══════════════════════════════════════════════════════════════════
   Composant principal
═══════════════════════════════════════════════════════════════════ */
export default function Relances() {
  injectStyles();
  const { t } = useTranslation('relances');
  const navigate = useNavigate();

  const [urgents, setUrgents] = useState([]);
  const [penalites, setPenalites] = useState([]);
  const [loadingUrgents, setLoadingUrgents] = useState(true);
  const [loadingPenalites, setLoadingPenalites] = useState(false);
  const [activeTab, setActiveTab] = useState('bureau');
  const [panelNotification, setPanelNotification] = useState(null);

  /* Filtres Tab 1 */
  const [search, setSearch] = useState('');
  const [filtreNiveau, setFiltreNiveau] = useState(null);
  const [filtreCategorie, setFiltreCategorie] = useState(null);
  const [filtreBureau, setFiltreBureau] = useState(null);

  const penalitesLoaded = useRef(false);

  /* Un délai relancé sort de la liste le temps du repos (paramètre système
     RELANCE_REPOS_JOURS) : la garder avec un compteur « 1 relance » faisait
     relancer deux fois. La bascule permet de revoir les relancés sans attendre. */
  const [inclureRelancees, setInclureRelancees] = useState(false);

  /* ── Chargement ─────────────────────────────────────────────────── */
  const loadUrgents = useCallback(async () => {
    setLoadingUrgents(true);
    try {
      const r = await axiosInstance.get('/v1/delais/urgents', { params: { inclureRelancees } });
      setUrgents(r.data?.data ?? r.data ?? []);
    } catch {
      toast.error(i18n.t('relances:erreurs.chargementUrgents'));
    } finally { setLoadingUrgents(false); }
  }, [inclureRelancees]);

  const loadPenalites = useCallback(async () => {
    if (penalitesLoaded.current) return;
    penalitesLoaded.current = true;
    setLoadingPenalites(true);
    try {
      const r = await axiosInstance.get('/v1/delais/penalites');
      setPenalites(r.data?.data ?? r.data ?? []);
    } catch {
      toast.error(i18n.t('relances:erreurs.chargementPenalites'));
    } finally { setLoadingPenalites(false); }
  }, []);

  useEffect(() => { loadUrgents(); }, [loadUrgents]);
  useEffect(() => {
    if (activeTab === 'penalites') loadPenalites();
  }, [activeTab, loadPenalites]);

  /* ── KPIs ──────────────────────────────────────────────────────── */
  // Les trois cartes de niveau pilotent le filtre de l'onglet « Par bureau » :
  // le niveau est porte explicitement par la carte, jamais deduit du libelle.
  const kpis = useMemo(() => {
    const compte = (niveau) => urgents.filter(d => d.niveauAlerte === niveau).length;
    const carteNiveau = (niveau, label, suffix) => ({
      label,
      value: compte(niveau),
      color: NIVEAUX[niveau].color,
      bg: NIVEAUX[niveau].bg,
      icon: NIVEAUX[niveau].icon,
      suffix,
      active: filtreNiveau === niveau,
      onClick: () => {
        setFiltreNiveau(prev => (prev === niveau ? null : niveau));
        setActiveTab('bureau');
      },
    });
    return [
      { label: t('kpis.alertesActives'), value: urgents.length, color: '#374151', bg: '#f9fafb', icon: <BellFilled /> },
      carteNiveau('CRITIQUE',  t('kpis.critiques'),   t('kpis.critiquesSuffixe')),
      carteNiveau('URGENT',    t('kpis.urgents'),     t('kpis.urgentsSuffixe')),
      carteNiveau('ATTENTION', t('kpis.vigilance'),   t('kpis.vigilanceSuffixe')),
    ];
  }, [urgents, filtreNiveau, t]);

  /* ── Bureaux distincts (filtre) ──────────────────────────────────── */
  const bureauxOptions = useMemo(() => {
    const seen = new Map();
    urgents.forEach(d => {
      if (d.homologueRaisonSociale && !seen.has(d.homologueRaisonSociale)) {
        seen.set(d.homologueRaisonSociale, {
          value: d.homologueRaisonSociale,
          label: `${d.homologueRaisonSociale}${d.homologueCodePays ? ` (${d.homologueCodePays})` : ''}`,
        });
      }
    });
    return [...seen.values()];
  }, [urgents]);

  /* ── Données filtrées Tab 1 ──────────────────────────────────────── */
  const urgentsFiltres = useMemo(() => {
    const q = search.toLowerCase();
    return urgents.filter(d => {
      if (filtreNiveau && d.niveauAlerte !== filtreNiveau) return false;
      if (filtreCategorie && d.categorie !== filtreCategorie) return false;
      if (filtreBureau && d.homologueRaisonSociale !== filtreBureau) return false;
      if (q) {
        const hay = [d.sinistreNumeroLocal, d.libelleDelai, d.libelleDelaiEn, d.homologueRaisonSociale, d.categorie]
          .filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [urgents, search, filtreNiveau, filtreCategorie, filtreBureau]);

  const handleRelanceSent = () => {
    toast.success(t('relanceEnregistree'));
    setPanelNotification(null);
    loadUrgents();
  };

  /* ═════════════════════════════════════════════════════════════════
     Colonnes Tab 1 (Par bureau)
  ═════════════════════════════════════════════════════════════════ */
  const columnsBureau = [
    {
      title: t('colonnes.niveau'),
      dataIndex: 'niveauAlerte',
      width: 115,
      render: (v) => {
        const n = NIVEAUX[v] || NIVEAUX.NORMAL;
        return (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            fontSize: 11.5, fontWeight: 700, padding: '3px 9px', borderRadius: 6,
            background: n.bg, color: n.color, border: `1px solid ${n.border}`,
          }}>
            {React.cloneElement(n.icon, { style: { fontSize: 10 } })} {n.label}
          </span>
        );
      },
      export: (v) => NIVEAUX[v]?.label ?? v,
    },
    {
      title: t('colonnes.sinistre'),
      dataIndex: 'sinistreNumeroLocal',
      width: 145,
      render: (v, r) => (
        <Button
          type="link" size="small"
          style={{ padding: 0, fontFamily: 'ui-monospace,monospace', fontSize: 12.5, color: GREEN }}
          onClick={(e) => { e.stopPropagation(); navigate(`/sinistres/${r.sinistreTrackingId}`); }}
        >
          {v}
        </Button>
      ),
      export: (v) => v ?? '',
    },
    {
      title: t('colonnes.delai'),
      dataIndex: 'libelleDelai',
      render: (v, r) => <span style={{ fontSize: 12.5 }}>{libelleDelai(r)}</span>,
      export: (v, r) => libelleDelai(r) ?? '',
    },
    {
      title: t('colonnes.categorie'),
      dataIndex: 'categorie',
      width: 120,
      render: (v) => <Tag style={{ borderRadius: 10, fontSize: 11 }}>{CATEGORIES[v] || v}</Tag>,
      export: (v) => CATEGORIES[v] ?? v,
    },
    {
      title: t('colonnes.bureauHomologue'),
      dataIndex: 'homologueRaisonSociale',
      width: 185,
      render: (v, r) => v ? (
        <span style={{ fontSize: 12, color: '#374151' }}>
          {v}
          {r.homologueCodePays && (
            <Tag style={{ marginLeft: 6, fontSize: 10, borderRadius: 8 }}>{r.homologueCodePays}</Tag>
          )}
        </span>
      ) : <span style={{ color: '#9ca3af' }}>-</span>,
      export: (v, r) => v ? `${v}${r?.homologueCodePays ? ` (${r.homologueCodePays})` : ''}` : '',
    },
    {
      title: t('colonnes.echeance'),
      dataIndex: 'dateEcheance',
      width: 110,
      render: (v) => <span style={{ fontSize: 12, color: '#374151' }}>{formatDate(v)}</span>,
      format: 'date',
    },
    {
      title: t('colonnes.jours'),
      dataIndex: 'joursRestants',
      width: 88,
      align: 'center',
      render: (v) => {
        if (v == null) return '-';
        const over = v < 0;
        return (
          <span style={{
            fontSize: 12, fontWeight: 600,
            color: over ? '#dc2626' : v <= 5 ? '#ea580c' : '#374151',
          }}>
            <ClockCircleOutlined style={{ marginRight: 3, fontSize: 10 }} />
            {over ? t('jours.depasseCourt', { n: Math.abs(v) }) : t('jours.restants', { n: v })}
          </span>
        );
      },
      export: (v) => v != null ? (v < 0 ? t('jours.depasse', { n: Math.abs(v) }) : t('jours.restants', { n: v })) : '',
    },
    {
      title: t('colonnes.relances'),
      dataIndex: 'nombreAlertes',
      width: 82,
      align: 'center',
      render: (v) => v > 0 ? (
        <Tooltip title={t('relancesDejaEnvoyees', { count: v })}>
          <Tag color="blue" style={{ borderRadius: 10, cursor: 'default', margin: 0 }}>{v}</Tag>
        </Tooltip>
      ) : <span style={{ color: '#d1d5db' }}>0</span>,
      export: (v) => String(v ?? 0),
    },
    {
      title: '',
      exportable: false,
      width: 100,
      render: (_, r) => (
        <Button
          size="small"
          icon={<SendOutlined />}
          style={{ borderColor: GREEN, color: GREEN, borderRadius: 6 }}
          onClick={(e) => { e.stopPropagation(); setPanelNotification(r); }}
        >
          {t('relancer')}
        </Button>
      ),
    },
  ];

  /* ═════════════════════════════════════════════════════════════════
     Colonnes Tab 2 (Pénalités)
  ═════════════════════════════════════════════════════════════════ */
  const columnsPenalites = [
    {
      title: t('colonnes.sinistre'),
      dataIndex: 'sinistreNumeroLocal',
      width: 145,
      render: (v, r) => (
        <Button
          type="link" size="small"
          style={{ padding: 0, fontFamily: 'ui-monospace,monospace', fontSize: 12.5, color: GREEN }}
          onClick={() => navigate(`/sinistres/${r.sinistreTrackingId}`)}
        >
          {v}
        </Button>
      ),
      export: (v) => v ?? '',
    },
    {
      title: t('colonnes.delai'),
      dataIndex: 'libelleDelai',
      render: (v, r) => <span style={{ fontSize: 12.5 }}>{libelleDelai(r)}</span>,
      export: (v, r) => libelleDelai(r) ?? '',
    },
    {
      title: t('colonnes.categorie'),
      dataIndex: 'categorie',
      width: 120,
      render: (v) => <Tag style={{ borderRadius: 10, fontSize: 11 }}>{CATEGORIES[v] || v}</Tag>,
      export: (v) => CATEGORIES[v] ?? v,
    },
    {
      title: t('colonnes.joursDepasses'),
      dataIndex: 'joursRetard',
      width: 120,
      align: 'center',
      render: (v) => <span style={{ color: '#dc2626', fontWeight: 600 }}>{v ? t('jours.restants', { n: v }) : '-'}</span>,
      export: (v) => v ? t('jours.restants', { n: v }) : '',
    },
    {
      title: t('colonnes.penaliteCalculee'),
      dataIndex: 'penaliteCalculee',
      width: 165,
      align: 'right',
      render: (v) => <span style={{ fontWeight: 600, color: '#dc2626' }}>{v != null ? formatMontant(v) : '-'}</span>,
      format: 'currency',
    },
    {
      title: t('colonnes.referenceLegale'),
      dataIndex: 'referenceJuridique',
      width: 145,
      render: (v) => v ? (
        <span style={{
          fontFamily: 'ui-monospace,monospace', fontSize: 11.5, color: '#78350f',
          background: '#fef3c7', padding: '2px 8px', borderRadius: 5,
        }}>
          {v}
        </span>
      ) : '-',
    },
  ];

  /* ═════════════════════════════════════════════════════════════════
     Render
  ═════════════════════════════════════════════════════════════════ */
  return (
    <Layout title={t('titre')}>
      <div>

        {/* Porte-onglets : chaque onglet a sa propre liste (delais urgents,
            penalites, bilaterales, historique, par organisme). Aucun compte unique
            ne vaut pour la page, donc pas de prop `compte`. */}
        <PageHeader
          titre={t('titre')}
          icone={<BellFilled />}
          actions={(
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                loadUrgents();
                penalitesLoaded.current = false;
                if (activeTab === 'penalites') loadPenalites();
              }}
              size="middle"
              style={{ borderRadius: 6 }}
            >
              {t('actualiser')}
            </Button>
          )}
        />

        {/* KPIs */}
        {loadingUrgents ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
            <Spin tip={t('chargementAlertes')} />
          </div>
        ) : (
          <KpiCards items={kpis} columns={4} style={{ marginBottom: 22 }} />
        )}

        {/* Onglets */}
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          size="middle"
          tabBarStyle={{ marginBottom: 0 }}
          items={[
            /* ── Tab 1 : Par bureau ─────────────────────────────── */
            {
              key: 'bureau',
              label: (
                <span>
                  {t('onglets.parBureau')}
                  {urgents.length > 0 && (
                    <span style={{
                      marginLeft: 8, fontSize: 11, fontWeight: 700,
                      background: '#dc2626', color: '#fff',
                      padding: '1px 7px', borderRadius: 10,
                    }}>{urgents.length}</span>
                  )}
                </span>
              ),
              children: (
                <div style={{ paddingTop: 14 }}>
                  {/* Filtres */}
                  <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                    <Input
                      prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
                      placeholder={t('filtres.recherche')}
                      allowClear
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      style={{ width: 240, borderRadius: 6 }}
                    />
                    <Select
                      placeholder={t('filtres.niveau')}
                      allowClear
                      value={filtreNiveau}
                      onChange={setFiltreNiveau}
                      style={{ width: 155 }}
                      options={Object.entries(NIVEAUX).map(([k, v]) => ({ value: k, label: v.label }))}
                    />
                    <Select
                      placeholder={t('filtres.categorie')}
                      allowClear
                      value={filtreCategorie}
                      onChange={setFiltreCategorie}
                      style={{ width: 155 }}
                      options={Object.entries(CATEGORIES).map(([k, v]) => ({ value: k, label: v }))}
                    />
                    <Select
                      placeholder={t('filtres.bureau')}
                      allowClear
                      showSearch
                      filterOption={filtreSansAccents}
                      value={filtreBureau}
                      onChange={setFiltreBureau}
                      style={{ width: 200 }}
                      options={bureauxOptions}
                    />
                    <Tooltip title={t('filtres.inclureRelanceesAide')}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#6b7280' }}>
                        <Switch size="small" checked={inclureRelancees} onChange={setInclureRelancees} />
                        {t('filtres.inclureRelancees')}
                      </span>
                    </Tooltip>
                    {(filtreNiveau || filtreCategorie || filtreBureau || search) && (
                      <Button
                        onClick={() => { setFiltreNiveau(null); setFiltreCategorie(null); setFiltreBureau(null); setSearch(''); }}
                        style={{ borderRadius: 6, color: '#6b7280' }}
                      >
                        {t('filtres.reinitialiser')}
                      </Button>
                    )}
                  </div>

                  <TableExportButton
                    floating
                    data={urgentsFiltres}
                    columns={columnsBureau}
                    filename="relances-delais-urgents"
                    title={t('export.urgentsTitre')}
                    subtitle={t('export.urgentsSousTitre', { count: urgentsFiltres.length })}
                    count={urgentsFiltres.length}
                  />

                  <Table
                    rowKey={(r) => r.id || r.trackingId || r.sinistreNumeroLocal}
                    loading={loadingUrgents}
                    dataSource={urgentsFiltres}
                    columns={columnsBureau}
                    size="middle"
                    pagination={{ pageSize: 25, showTotal: (n) => t('totalDelais', { count: n }) }}
                    onRow={(r) => ({
                      className: [
                        'rl-row',
                        panelNotification?.id === r.id ? 'rl-row-selected' : '',
                      ].join(' ').trim(),
                      onClick: () => setPanelNotification(r),
                    })}
                    locale={{
                      emptyText: (
                        <div style={{ padding: '32px 0', textAlign: 'center', color: '#9ca3af' }}>
                          <CheckCircleOutlined style={{ fontSize: 28, marginBottom: 8, display: 'block', color: '#15803d' }} />
                          {t('vide.aucunDelai')}
                        </div>
                      ),
                    }}
                  />
                </div>
              ),
            },

            /* ── Tab 2 : Pénalités ──────────────────────────────── */
            {
              key: 'penalites',
              label: t('onglets.penalites'),
              children: (
                <div style={{ paddingTop: 14 }}>
                  <TableExportButton
                    floating
                    data={penalites}
                    columns={columnsPenalites}
                    filename="penalites-cedeao"
                    title={t('export.penalitesTitre')}
                    subtitle={t('export.penalitesSousTitre', { count: penalites.length })}
                    count={penalites.length}
                  />
                  <Table
                    rowKey={(r) => r.id || r.sinistreTrackingId}
                    loading={loadingPenalites}
                    dataSource={penalites}
                    columns={columnsPenalites}
                    size="middle"
                    pagination={{ pageSize: 25, showTotal: (n) => t('totalDossiers', { count: n }) }}
                    locale={{
                      emptyText: (
                        <div style={{ padding: '32px 0', textAlign: 'center', color: '#9ca3af' }}>
                          <CheckCircleOutlined style={{ fontSize: 28, marginBottom: 8, display: 'block', color: '#15803d' }} />
                          {t('vide.aucunePenalite')}
                        </div>
                      ),
                    }}
                  />
                </div>
              ),
            },

            /* ── Tab 3 : Bilatérales ────────────────────────────── */
            {
              key: 'bilaterales',
              label: t('onglets.bilaterales'),
              children: <RelancesBilateralesTab />,
            },

            /* ── Tab 4 : Par partenaire ─────────────────────────── */
            {
              key: 'partenaires',
              label: t('onglets.parPartenaire'),
              children: <RelancesParOrganismeTab />,
            },

            /* ── Tab 5 : Historique ─────────────────────────────── */
            {
              key: 'historique',
              label: t('onglets.historique'),
              children: <HistoriqueRelancesTab />,
            },
          ]}
        />
      </div>

      {/* ── Modale de relance ────────────────────────────────────── */}
      <Modal
        open={!!panelNotification}
        width={640}
        onCancel={() => setPanelNotification(null)}
        footer={null}
        destroyOnClose
        styles={{ body: { padding: 0 } }}
      >
        {panelNotification && (
          <RelancePanel
            notification={panelNotification}
            onClose={() => setPanelNotification(null)}
            onSent={handleRelanceSent}
          />
        )}
      </Modal>
    </Layout>
  );
}
